/**
 * Submission Analysis Polling Module
 * Handles real-time status updates and countdown timer
 */

(function() {
  'use strict';

  const SubmissionAnalyzer = {
    submissionId: null,
    themeName: null,
    pollInterval: null,
    pollCount: 0,
    maxPolls: 120, // Poll for up to 10 minutes (5-second intervals)

    init() {
      console.log('[SubmissionAnalyzer] Initializing...');

      // Get submission data from global
      if (!window.submissionAnalysis || !window.submissionAnalysis.submissionId) {
        console.error('[SubmissionAnalyzer] No submission ID found');
        this.showError('Invalid submission data. Please try again.');
        return;
      }

      this.submissionId = window.submissionAnalysis.submissionId;
      this.themeName = window.submissionAnalysis.themeName || 'Platform';

      // Start polling
      this.startPolling();
    },

    startPolling() {
      console.log('[SubmissionAnalyzer] Starting status polling...');

      // Poll immediately
      this.checkStatus();

      // Then poll every 5 seconds
      this.pollInterval = setInterval(() => {
        this.pollCount++;

        if (this.pollCount >= this.maxPolls) {
          console.warn('[SubmissionAnalyzer] Max polling attempts reached');
          clearInterval(this.pollInterval);
          this.showError('Analysis is taking longer than expected. Please check back later or contact support.');
          return;
        }

        this.checkStatus();
      }, 5000);
    },

    async checkStatus() {
      try {
        const response = await fetch('/api/submissions/check-review-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            submission_id: this.submissionId
          })
        });

        const data = await response.json();

        if (!data.success) {
          console.error('[SubmissionAnalyzer] API error:', data.message);
          this.showError(data.message || 'Failed to check submission status');
          this.stopPolling();
          return;
        }

        this.handleStatusUpdate(data.data);

      } catch (error) {
        console.error('[SubmissionAnalyzer] Network error:', error);
        // Don't stop polling on network errors - might be temporary
      }
    },

    handleStatusUpdate(data) {
      console.log('[SubmissionAnalyzer] Status update:', data);

      const { status, message, analysis_complete } = data;

      // Update status message
      this.updateStatusMessage(message, status);

      // Update progress bar
      this.updateProgress(data);

      // Check if analysis is complete
      if (analysis_complete) {
        console.log('[SubmissionAnalyzer] Analysis complete!');
        this.stopPolling();
        this.showResults(data);
      }
    },

    updateStatusMessage(message, status) {
      const statusEl = document.getElementById('status-message');
      if (!statusEl) return;

      const titleEl = statusEl.querySelector('h3');
      const detailEl = document.getElementById('status-detail');

      if (titleEl) titleEl.textContent = message;
      if (detailEl) detailEl.textContent = this.getStatusDetail(status);
    },

    getStatusDetail(status) {
      const details = {
        'pending': 'Your submission is being prepared for analysis',
        'queued': 'Analysis request is queued and will begin shortly',
        'submitted': 'Analysis has been submitted to our AI system',
        'processing': 'Our AI is analyzing your content frame by frame',
        'completed': 'Analysis is complete'
      };

      return details[status] || 'Processing your submission...';
    },

    updateProgress(data) {
      const progressBar = document.getElementById('analysis-progress');
      if (!progressBar) return;

      // Calculate progress based on time or status
      let progress = 10;

      if (data.analysis_complete) {
        progress = 100;
      } else if (data.status === 'processing') {
        // Progress between 30-90% while processing
        progress = 30 + (this.pollCount * 0.5); // Gradual increase based on poll count
      } else if (data.status === 'submitted') {
        progress = 20;
      } else if (data.status === 'queued') {
        progress = 15;
      }

      progressBar.style.width = `${progress}%`;
    },

    showResults(data) {
      const { approved_for_bloodstream, reasoning, platform_suitability } = data;

      // Disable navigation warning - analysis complete
      if (typeof window.disableNavigationWarning === 'function') {
        window.disableNavigationWarning();
      }

      // Update page title
      const pageTitle = document.getElementById('page-title');
      if (pageTitle) {
        pageTitle.textContent = 'Submission Result';
      }

      // Hide the warning notice
      const analysisNotice = document.getElementById('analysis-notice');
      if (analysisNotice) {
        analysisNotice.style.display = 'none';
      }

      // Hide the status card entirely
      const statusCard = document.getElementById('status-card');
      if (statusCard) {
        statusCard.style.display = 'none';
      }

      // Copy submission ID to result cards
      const submissionId = this.submissionId;
      const resultSubmissionIds = document.querySelectorAll('.result-submission-id');
      resultSubmissionIds.forEach(el => {
        el.textContent = submissionId;
      });

      // Show results section
      const resultsSection = document.getElementById('results-section');
      if (resultsSection) resultsSection.style.display = 'block';

      if (approved_for_bloodstream) {
        this.showApproved();
      } else {
        this.showRejected(reasoning);
      }

      // Show platform details if available
      if (platform_suitability) {
        this.showPlatformDetails(platform_suitability);
      }
    },

    showApproved() {
      const approvedResult = document.getElementById('approved-result');
      if (approvedResult) {
        approvedResult.style.display = 'block';
      }

      // Update progress to 100%
      const progressBar = document.getElementById('analysis-progress');
      if (progressBar) {
        progressBar.style.width = '100%';
        progressBar.classList.remove('progress-bar-animated');
      }
    },

    showRejected(reasoning) {
      const rejectedResult = document.getElementById('rejected-result');
      const reasoningEl = document.getElementById('rejection-reasoning');

      if (rejectedResult) {
        rejectedResult.style.display = 'block';
      }

      if (reasoningEl && reasoning) {
        reasoningEl.textContent = reasoning;
      }

      // Update progress bar to warning state
      const progressBar = document.getElementById('analysis-progress');
      if (progressBar) {
        progressBar.style.width = '100%';
        progressBar.classList.remove('progress-bar-animated', 'bg-primary');
        progressBar.classList.add('bg-warning');
      }
    },

    showError(message) {
      const errorSection = document.getElementById('error-section');
      const errorMessage = document.getElementById('error-message');
      const loadingEl = document.getElementById('analysis-loading');
      const statusMessageEl = document.getElementById('status-message');

      // Disable navigation warning - error occurred
      if (typeof window.disableNavigationWarning === 'function') {
        window.disableNavigationWarning();
      }

      if (loadingEl) loadingEl.style.display = 'none';
      if (statusMessageEl) statusMessageEl.style.display = 'none';

      if (errorMessage) errorMessage.textContent = message;
      if (errorSection) errorSection.style.display = 'block';

      this.stopPolling();
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    }
  };

  // Initialize on DOM ready - only if on the analyzing page
  const shouldInit = () => {
    // Check if we're on the analyzing page by looking for required elements
    const hasAnalysisElements = document.getElementById('analysis-loading') ||
                                 document.getElementById('status-card');

    // Also check if submissionAnalysis data exists
    const hasSubmissionData = window.submissionAnalysis && window.submissionAnalysis.submissionId;

    return hasAnalysisElements && hasSubmissionData;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (shouldInit()) {
        SubmissionAnalyzer.init();
      }
    });
  } else {
    if (shouldInit()) {
      SubmissionAnalyzer.init();
    }
  }

  // Export for debugging
  window.SubmissionAnalyzer = SubmissionAnalyzer;

})();
